import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import * as THREE from "three";
import { FontLoader } from "three/examples/jsm/loaders/FontLoader";
import { TextGeometry } from "three/examples/jsm/geometries/TextGeometry";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";
import randomBackground from "../randomBackground";
import { playRandomTrack } from "../helpers/music";

// Dynamically import a font from three examples CDN as fallback if no local font.
// (Could be replaced with a locally hosted font JSON for offline use.)
const FONT_URL = "/fonts/helvetiker_regular.typeface.json";

const Home: React.FC = () => {
  const mountRef = useRef<HTMLDivElement | null>(null);

  const navigate = useNavigate();

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;

    // Dynamically generate textures for cube faces using randomBackground utility.
    function ensureHiddenCanvas() {
      let existing = document.getElementById(
        "displayCanvas"
      ) as HTMLCanvasElement | null;
      if (!existing) {
        const c = document.createElement("canvas");
        c.id = "displayCanvas";
        c.width = 512;
        c.height = 512;
        c.style.position = "fixed";
        c.style.left = "-9999px";
        c.style.top = "-9999px";
        document.body.appendChild(c);
        existing = c;
      }
      return existing;
    }
    const hiddenCanvas = ensureHiddenCanvas();

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0xa020f0);

    const camera = new THREE.PerspectiveCamera(
      60,
      mount.clientWidth / mount.clientHeight,
      0.1,
      1000
    );

    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setPixelRatio(window.devicePixelRatio);
    renderer.setSize(mount.clientWidth, mount.clientHeight);
    if ("toneMappingExposure" in renderer) {
      (renderer as any).toneMappingExposure = 1.3; // brighten overall output
    }
    // Set renderer output color space if supported (three r152+)
    if ((renderer as any).outputColorSpace && (THREE as any).SRGBColorSpace) {
      (renderer as any).outputColorSpace = (THREE as any).SRGBColorSpace;
    }
    mount.appendChild(renderer.domElement);

    const ambient = new THREE.AmbientLight(0xffffff, 0.9); // brighter ambient
    scene.add(ambient);
    const dir = new THREE.DirectionalLight(0xffffff, 0.7);
    dir.position.set(15, 25, 12);
    scene.add(dir);

    // Additional fill lights distributed around the cube for more even illumination
    const fillIntensity = 0.35;
    const pointPositions: [number, number, number][] = [
      [25, 25, 25],
      [-25, 25, 25],
      [25, -25, 25],
      [-25, -25, 25],
      [25, 25, -25],
      [-25, 25, -25],
      [25, -25, -25],
      [-25, -25, -25],
    ];
    pointPositions.forEach(([x, y, z]) => {
      const p = new THREE.PointLight(0xffffff, fillIntensity, 160, 2);
      p.position.set(x, y, z);
      scene.add(p);
    });

    // Placeholder room; will be replaced with textured materials once generated.
    const roomGeometry = new THREE.BoxGeometry(100, 100, 100);
    let roomMaterials: THREE.MeshBasicMaterial[] = [
      new THREE.MeshBasicMaterial({ color: 0xffffff, side: THREE.BackSide }),
      new THREE.MeshBasicMaterial({ color: 0xffffff, side: THREE.BackSide }),
      new THREE.MeshBasicMaterial({ color: 0xffffff, side: THREE.BackSide }),
      new THREE.MeshBasicMaterial({ color: 0xffffff, side: THREE.BackSide }),
      new THREE.MeshBasicMaterial({ color: 0xffffff, side: THREE.BackSide }),
      new THREE.MeshBasicMaterial({ color: 0xffffff, side: THREE.BackSide }),
    ];
    const room = new THREE.Mesh(roomGeometry, roomMaterials);
    scene.add(room);
    // Purple outline of cube edges
    const roomEdgesGeometry = new THREE.EdgesGeometry(roomGeometry);
    const roomEdgeMaterial = new THREE.LineBasicMaterial({
      color: 0xa020f0,
      transparent: true,
      opacity: 0.95,
      depthWrite: false,
      depthTest: false, // draw on top
    });
    const roomEdgeLines = new THREE.LineSegments(
      roomEdgesGeometry,
      roomEdgeMaterial
    );
    // Slightly scale outward so edges don't z-fight with faces
    roomEdgeLines.scale.setScalar(1.002);
    // Ensure rendered after other objects
    roomEdgeLines.renderOrder = 999;
    // Parent to room so it rotates with cube
    room.add(roomEdgeLines);
    let targetRoomRotationY = 0; // user offset rotation (via arrow keys / horizontal touch)
    let targetRoomRotationX = 0; // vertical touch drag tilt
    const autoRotateSpeed = 0.15; // radians per second for slow continuous spin
    // Device motion orientation offsets (added to user offsets)
    let motionRotationY = 0; // yaw from device tilt (gamma)
    let motionRotationX = 0; // pitch from device tilt (beta)
    let motionActive = false; // true once listener attached
    let motionRequested = false; // prevent duplicate permission requests

    async function generateRoomTextures() {
      // Generate 6 data URLs (can reuse some for performance if desired)
      const loader = new THREE.TextureLoader();

      Array.from({ length: 6 }, (val, i) => {
        const url = randomBackground();
        loader.load(url, (tex) => {
          if ("colorSpace" in tex && (THREE as any).SRGBColorSpace) {
            (tex as any).colorSpace = (THREE as any).SRGBColorSpace;
          }
          tex.needsUpdate = true;
          const mat = roomMaterials[i];
          mat.map = tex;
          mat.needsUpdate = true;
        });
      });
    }

    generateRoomTextures();

    // Add hemisphere light to softly illuminate interior of cube
    const hemi = new THREE.HemisphereLight(0xdddddd, 0x222222, 0.5);
    scene.add(hemi);

    // --- Animated Character (GLB) Setup ---
    const gltfLoader = new GLTFLoader();
    let characterRoot: THREE.Object3D | null = null;
    let mixer: THREE.AnimationMixer | null = null;
    let walkAction: THREE.AnimationAction | null = null;
    const characterTarget = new THREE.Vector3();
    const tempDir = new THREE.Vector3();
    const CHARACTER_SPEED = 6; // units per second
    const CHARACTER_MARGIN = 40; // stay within +/- (50 - margin) on X/Z
    const FLOOR_Y = -50; // bottom interior face of 100-sized cube centered at origin
    let characterFloorY = FLOOR_Y; // will be refined after model load (accounts for model minY)
    let characterReady = false;

    function chooseNewCharacterTarget() {
      characterTarget.set(
        (Math.random() * 2 - 1) * CHARACTER_MARGIN,
        characterFloorY,
        (Math.random() * 2 - 1) * CHARACTER_MARGIN
      );
    }

    gltfLoader.load(
      "/3dmodels/RobotExpressive.glb",
      (gltf) => {
        characterRoot = gltf.scene;
        if (!characterRoot) return;
        // Parent to room so it inherits rotation of cube
        room.add(characterRoot);
        // Compute scale so character fits nicely inside (target height ~8 units)
        const bbox = new THREE.Box3().setFromObject(characterRoot);
        const size = new THREE.Vector3();
        bbox.getSize(size);
        if (size.y > 0) {
          const desiredHeight = 8;
          const scale = desiredHeight / size.y;
          characterRoot.scale.setScalar(scale);
        }
        characterRoot.traverse((n: any) => {
          if (n.isMesh) {
            n.castShadow = false;
            n.receiveShadow = false;
            if (n.material) {
              if (Array.isArray(n.material)) {
                n.material.forEach(
                  (m: any) => (m.transparent = !!m.alphaTest || m.transparent)
                );
              } else {
                (n.material as any).transparent =
                  !!(n.material as any).alphaTest ||
                  (n.material as any).transparent;
              }
            }
          }
        });
        // Recompute bounds after scaling so we can align feet to floor (y = -50)
        const bbox2 = new THREE.Box3().setFromObject(characterRoot);
        const center = new THREE.Vector3();
        bbox2.getCenter(center);
        // Center model around origin first (mostly for X/Z) so rotation behaves nicely
        characterRoot.position.sub(center);
        // After centering, compute new bounds to find minY (foot level in local space)
        const bbox3 = new THREE.Box3().setFromObject(characterRoot);
        const minY = bbox3.min.y;
        // Shift upward/downward so feet sit exactly on the interior floor plane (y = -50)
        const footShift = FLOOR_Y - minY;
        characterRoot.position.y += footShift;
        characterFloorY = FLOOR_Y; // store final floor Y (feet resting)
        // Randomize initial horizontal placement on floor
        characterRoot.position.x = (Math.random() * 2 - 1) * CHARACTER_MARGIN;
        characterRoot.position.z = (Math.random() * 2 - 1) * CHARACTER_MARGIN;
        chooseNewCharacterTarget(); // sets target on floor plane
        // Animations
        if (gltf.animations && gltf.animations.length) {
          mixer = new THREE.AnimationMixer(characterRoot);
          // Try to find a walk/run clip
          const walkClip =
            gltf.animations.find((c) => /walk|run/i.test(c.name)) ||
            gltf.animations[0];
          walkAction = mixer.clipAction(walkClip);
          walkAction.play();
        }
        characterReady = true;
      },
      undefined,
      (err) => {
        console.warn("Failed to load character GLB", err);
      }
    );

    let textMesh: THREE.Mesh | null = null;
    let glowMesh: THREE.Mesh | null = null;
    let buttonPlane: THREE.Mesh | null = null;
    let buttonPlaneEdges: THREE.LineSegments | null = null;
    let textBox: THREE.Box3 | null = null; // store bounding box for resize recalculations
    let interactiveTarget: THREE.Object3D | null = null; // what we raycast against
    // Ephemeral cube (appears/disappears every 3s) resources
    const ephemeralSize = 2;
    const ephemeralGeometry = new THREE.BoxGeometry(
      ephemeralSize,
      ephemeralSize,
      ephemeralSize
    );
    const ephemeralMaterial = new THREE.MeshStandardMaterial({
      color: 0xa020f0,
      emissive: 0x551177,
      emissiveIntensity: 1.2,
      metalness: 0.25,
      roughness: 0.35,
    });
    const ephemeralCube = new THREE.Mesh(ephemeralGeometry, ephemeralMaterial);
    ephemeralCube.visible = false; // start hidden
    scene.add(ephemeralCube);
    // Add bright edge outline to emphasize rotation
    const ephemeralEdgeGeo = new THREE.EdgesGeometry(ephemeralGeometry);
    const ephemeralEdgeMat = new THREE.LineBasicMaterial({
      color: 0xffffff,
      transparent: true,
      opacity: 0.9,
      depthWrite: false,
    });
    const ephemeralEdgeLines = new THREE.LineSegments(
      ephemeralEdgeGeo,
      ephemeralEdgeMat
    );
    ephemeralCube.add(ephemeralEdgeLines);
    // Pulsing glow configuration for ephemeral cube
    const ephemeralPulseFrequency = 1.5; // pulses per second
    const ephemeralBaseEmissive = 0.5; // minimum emissive intensity when visible
    const ephemeralPulseAmplitude = 1.1; // added on top of base at pulse peak

    // --- Device Orientation Handling ---
    function handleOrientation(e: DeviceOrientationEvent) {
      // beta: device front-back tilt (-180..180), gamma: left-right tilt (-90..90)
      const beta = e.beta ?? 0; // front-back
      const gamma = e.gamma ?? 0; // left-right
      // Convert to radians and scale down for subtle motion.
      const betaRad = THREE.MathUtils.degToRad(beta);
      const gammaRad = THREE.MathUtils.degToRad(gamma);
      const maxPitch = Math.PI / 4; // same limit as user tilt
      motionRotationX = THREE.MathUtils.clamp(
        betaRad * 0.35,
        -maxPitch,
        maxPitch
      );
      motionRotationY = gammaRad * 0.45; // allow gentle yaw; no hard clamp for fluidity
    }

    function enableMotionIfPossible() {
      if (motionRequested) return;
      motionRequested = true;
      const w: any = window as any;
      // iOS requires explicit permission request after user gesture.
      const permissionFns: Array<() => Promise<string>> = [];
      if (
        w.DeviceOrientationEvent &&
        w.DeviceOrientationEvent.requestPermission
      ) {
        permissionFns.push(() => w.DeviceOrientationEvent.requestPermission());
      }
      if (w.DeviceMotionEvent && w.DeviceMotionEvent.requestPermission) {
        permissionFns.push(() => w.DeviceMotionEvent.requestPermission());
      }
      if (!permissionFns.length) {
        // Non iOS or no permission API needed.
        window.addEventListener("deviceorientation", handleOrientation);
        motionActive = true;
        return;
      }
      (async () => {
        for (const req of permissionFns) {
          try {
            const res = await req();
            if (res === "granted") {
              window.addEventListener("deviceorientation", handleOrientation);
              motionActive = true;
              return;
            }
          } catch (err) {
            // eslint-disable-next-line no-console
            console.warn("Motion permission denied or failed", err);
          }
        }
      })();
    }

    let currentAudio: HTMLAudioElement | undefined;

    let lastEphemeralVisible = false;
    const ephemeralCycleSeconds = 6; // 3s on, 3s off cycle length
    const ephemeralHalfCycle = ephemeralCycleSeconds / 2; // 3
    function randomizeEphemeralPosition() {
      // Keep within interior of 100 cube with some margin from walls
      const margin = 15;
      const range = 50 - margin; // half-size (50) minus margin
      ephemeralCube.position.set(
        (Math.random() * 2 - 1) * range,
        (Math.random() * 2 - 1) * range,
        (Math.random() * 2 - 1) * range
      );
      ephemeralCube.rotation.set(
        Math.random() * Math.PI,
        Math.random() * Math.PI,
        Math.random() * Math.PI
      );
    }
    const marginFactor = 1.15; // >1 adds extra space around text
    let baseDistance = 0; // distance computed to fit text
    let zoomScale = 4; // multiplicative zoom factor (changed via arrow up/down)
    const loader = new FontLoader();
    loader.load(
      FONT_URL,
      (font: any) => {
        const extrudeDepth = 0.5; // Reduced depth so it isn't a long tunnel
        const textGeo = new TextGeometry("resume", {
          font,
          size: 4,
          depth: extrudeDepth,
          curveSegments: 12,
          bevelEnabled: true,
          bevelThickness: 0.05,
          bevelSize: 0.06,
          bevelOffset: 0,
          bevelSegments: 3,
        });
        // Center along all axes so text floats in middle of cube
        textGeo.center();
        // Lightened base color & slightly higher envMapIntensity for clearer readability.
        const material = new THREE.MeshStandardMaterial({
          color: 0xc050ff, // was 0xa020f0 (darker purple)
          metalness: 0.42,
          roughness: 0.28,
          emissive: 0x120a1c, // subtle emissive to lift shadows
          envMapIntensity: 1.0,
        });
        textMesh = new THREE.Mesh(textGeo, material);
        textMesh.name = "resumeText";
        scene.add(textMesh);

        // Compute bounding box for button plane sizing
        textBox = textGeo.boundingBox ? textGeo.boundingBox.clone() : null;
        if (textBox) {
          const width = textBox.max.x - textBox.min.x;
          const height = textBox.max.y - textBox.min.y;
          const padX = width * 0.4; // horizontal padding
          const padY = height * 0.6; // vertical padding
          const planeGeo = new THREE.PlaneGeometry(
            width + padX,
            height + padY,
            1,
            1
          );
          const planeMat = new THREE.MeshBasicMaterial({
            color: 0x14051f,
            transparent: true,
            opacity: 0.85,
            depthWrite: false,
          });
          buttonPlane = new THREE.Mesh(planeGeo, planeMat);
          buttonPlane.position.z = -0.35; // behind text
          scene.add(buttonPlane);
          // Edge outline for plane
          const edgeGeo = new THREE.EdgesGeometry(planeGeo);
          const edgeMat = new THREE.LineBasicMaterial({
            color: 0xa020f0,
            transparent: true,
            opacity: 0.85,
            depthWrite: false,
          });
          buttonPlaneEdges = new THREE.LineSegments(edgeGeo, edgeMat);
          buttonPlaneEdges.position.copy(buttonPlane.position);
          buttonPlaneEdges.renderOrder = 5;
          scene.add(buttonPlaneEdges);
        }

        // Glow duplicate mesh for hover effect
        const glowMat = new THREE.MeshBasicMaterial({
          color: 0xc94dff,
          transparent: true,
          opacity: 0,
          blending: THREE.AdditiveBlending,
          depthWrite: false,
        });
        glowMesh = new THREE.Mesh(textGeo.clone(), glowMat);
        glowMesh.scale.setScalar(1.06);
        scene.add(glowMesh);

        // Set interactive target (prefer wider plane when present)
        interactiveTarget = buttonPlane || textMesh;

        // Bounding box already computed above.
        if (!textBox) {
          textBox = textGeo.boundingBox ? textGeo.boundingBox.clone() : null;
        }
        fitCameraToText(extrudeDepth);
      },
      undefined,
      (err: unknown) => {
        console.error("Failed to load font", err);
      }
    );

    const clock = new THREE.Clock();

    // --- Ephemeral Cube Explosion State ---
    let explosionActive = false;
    let explosionStart = 0;
    const explosionDuration = 1.3; // seconds
    let fragmentGroup: THREE.Group | null = null;
    let fragmentGeometry: THREE.BoxGeometry | null = null;
    interface FragmentData {
      mesh: THREE.Mesh;
      velocity: THREE.Vector3;
      mat: THREE.MeshStandardMaterial;
    }
    let fragments: FragmentData[] = [];

    function triggerEphemeralExplosion() {
      if (explosionActive) return;
      explosionActive = true;
      explosionStart = clock.getElapsedTime();
      // Hide original cube during explosion
      ephemeralCube.visible = false;
      // Tear down any prior fragments just in case
      if (fragmentGroup) {
        fragmentGroup.children.forEach((c) => fragmentGroup!.remove(c));
      }
      fragments = [];
      if (!fragmentGroup) {
        fragmentGroup = new THREE.Group();
        scene.add(fragmentGroup);
      }
      // Shared geometry for fragments
      if (fragmentGeometry) {
        fragmentGeometry.dispose();
      }
      fragmentGeometry = new THREE.BoxGeometry(
        ephemeralSize / 3,
        ephemeralSize / 3,
        ephemeralSize / 3
      );
      const fragmentCount = 14; // a handful of shards
      for (let i = 0; i < fragmentCount; i++) {
        const mat = new THREE.MeshStandardMaterial({
          color: 0xd8b0ff,
          emissive: 0x602090,
          emissiveIntensity: 1.0,
          metalness: 0.35,
          roughness: 0.4,
          transparent: true,
          opacity: 1,
        });
        const shard = new THREE.Mesh(fragmentGeometry, mat);
        shard.position.copy(ephemeralCube.position);
        fragmentGroup.add(shard);
        // Random velocity vector bias slightly upward
        const dir = new THREE.Vector3(
          Math.random() * 2 - 1,
          Math.random() * 1.2 + 0.2,
          Math.random() * 2 - 1
        ).normalize();
        const speed = 2 + Math.random() * 3.2; // 2 - 5.2 units/sec
        fragments.push({
          mesh: shard,
          velocity: dir.multiplyScalar(speed),
          mat,
        });
      }
    }

    let isHoveringText = false;
    let hoverChanged = false;
    let currentGlowOpacity = 0;
    let targetGlowOpacity = 0;
    let targetBaseScale = 1;
    let currentBaseScale = 1;

    function animate() {
      requestAnimationFrame(animate);
      const delta = clock.getDelta(); // time since last frame
      const t = clock.getElapsedTime();
      if (textMesh) {
        // Keep text stationary in rotation; only apply gentle vertical float.
        textMesh.position.y = Math.sin(t * 1.2) * 0.5;
        // Update hover targets
        targetGlowOpacity = isHoveringText ? 0.85 : 0;
        targetBaseScale = isHoveringText ? 1.05 : 1;
        // Ease glow opacity
        currentGlowOpacity = THREE.MathUtils.lerp(
          currentGlowOpacity,
          targetGlowOpacity,
          0.12
        );
        if (glowMesh) {
          (glowMesh.material as THREE.MeshBasicMaterial).opacity =
            currentGlowOpacity;
          glowMesh.visible = currentGlowOpacity > 0.01;
        }
        // Base scale + gentle pulse overlay
        currentBaseScale = THREE.MathUtils.lerp(
          currentBaseScale,
          targetBaseScale,
          0.15
        );
        const pulse = 1 + Math.sin(t * 2.2) * 0.015; // 1.5% oscillation
        const finalScale = currentBaseScale * pulse;
        textMesh.scale.set(finalScale, finalScale, finalScale);
        if (glowMesh) glowMesh.scale.setScalar(finalScale * 1.06);
        // Adjust emissive color once per hover change
        if (
          hoverChanged &&
          textMesh.material instanceof THREE.MeshStandardMaterial
        ) {
          const mat = textMesh.material as THREE.MeshStandardMaterial;
          if (isHoveringText) {
            mat.emissive.setHex(0x5c1b85); // brighter emissive on hover
            mat.color.setHex(0xe3b3ff); // lighter hover tint
          } else {
            mat.emissive.setHex(0x120a1c);
            mat.color.setHex(0xc050ff); // updated base color
          }
          hoverChanged = false;
        }
      }
      // Continuous base rotation plus user-controlled offset.
      const desiredY = t * autoRotateSpeed + targetRoomRotationY;
      const appliedY = motionActive ? desiredY + motionRotationY : desiredY;
      room.rotation.y = THREE.MathUtils.lerp(room.rotation.y, appliedY, 0.1);
      // Lerp X (no auto-rotate on X, only user tilt) with damping.
      const appliedX = motionActive
        ? THREE.MathUtils.clamp(
            targetRoomRotationX + motionRotationX,
            -Math.PI / 3,
            Math.PI / 3
          )
        : targetRoomRotationX;
      room.rotation.x = THREE.MathUtils.lerp(room.rotation.x, appliedX, 0.12);

      // Ephemeral cube visibility cycle logic (3s visible, 3s hidden)
      const cyclePos = t % ephemeralCycleSeconds; // 0..6
      const shouldBeVisible = cyclePos < ephemeralHalfCycle; // first 3 seconds
      if (shouldBeVisible !== lastEphemeralVisible) {
        if (shouldBeVisible) {
          randomizeEphemeralPosition();
        }
        ephemeralCube.visible = shouldBeVisible;
        lastEphemeralVisible = shouldBeVisible;
      }
      // Rotate opposite direction to room while visible
      // Always rotate (so orientation changes while hidden too)
      ephemeralCube.rotation.y -= delta * (autoRotateSpeed * 6); // noticeably faster opposite spin
      ephemeralCube.rotation.x += delta * (autoRotateSpeed * 2.2);
      ephemeralCube.rotation.z += delta * (autoRotateSpeed * 1.5);

      // Pulsing glow (only when visible)
      if (ephemeralCube.visible) {
        const pulse =
          (Math.sin(t * ephemeralPulseFrequency * Math.PI * 2) + 1) * 0.5; // 0..1
        const mat = ephemeralCube.material as THREE.MeshStandardMaterial;
        // Clamp to avoid extreme spikes if user changes params
        mat.emissiveIntensity = THREE.MathUtils.clamp(
          ephemeralBaseEmissive + pulse * ephemeralPulseAmplitude,
          0,
          5
        );
        // Edge lines also pulse for clearer visual feedback
        ephemeralEdgeMat.opacity = 0.25 + pulse * 0.65; // range ~0.25 - 0.9
      }

      // Explosion fragment animation
      if (explosionActive) {
        const expElapsed = t - explosionStart;
        const progress = expElapsed / explosionDuration; // 0..1
        fragments.forEach((f) => {
          // Basic gravity
          f.velocity.y -= 4.2 * delta; // mild gravity pull
          // Damping slightly
          f.velocity.multiplyScalar(1 - 0.45 * delta);
          f.mesh.position.addScaledVector(f.velocity, delta);
          f.mesh.rotation.x += delta * 6;
          f.mesh.rotation.y += delta * 7;
          // Fade & emissive drop
          const fade = 1 - progress;
          f.mat.opacity = Math.max(0, fade);
          f.mat.emissiveIntensity = fade * 1.0;
        });
        if (expElapsed >= explosionDuration) {
          // Cleanup fragments
          fragments.forEach((f) => {
            fragmentGroup && fragmentGroup.remove(f.mesh);
            f.mat.dispose();
          });
          fragments = [];
          // Mark explosion done; allow normal cycle to bring cube back next visibility window
          explosionActive = false;
          // Force state so next visible half-cycle will reposition & show
          lastEphemeralVisible = false;
        }
      }
      // Animated character wandering logic
      if (characterReady && characterRoot) {
        // Move toward target
        tempDir.copy(characterTarget).sub(characterRoot.position);
        const dist = tempDir.length();
        if (dist < 1) {
          chooseNewCharacterTarget();
        } else {
          const move = Math.min(dist, CHARACTER_SPEED * delta);
          characterRoot.position.add(tempDir.normalize().multiplyScalar(move));
          // Face movement direction (y-axis rotation)
          const desiredYaw = Math.atan2(tempDir.x, tempDir.z);
          const currentYaw = characterRoot.rotation.y;
          let dyaw = desiredYaw - currentYaw;
          // Wrap to [-PI, PI]
          dyaw = ((dyaw + Math.PI) % (Math.PI * 2)) - Math.PI;
          characterRoot.rotation.y = currentYaw + dyaw * 0.15; // smooth turn
        }
        if (mixer) mixer.update(delta);
      }
      renderer.render(scene, camera);
    }
    animate();

    function fitCameraToText(extrudeDepth: number) {
      if (!textBox) return;
      const width = textBox.max.x - textBox.min.x;
      const height = textBox.max.y - textBox.min.y;
      const halfW = (width * marginFactor) / 2;
      const halfH = (height * marginFactor) / 2;
      const vFov = THREE.MathUtils.degToRad(camera.fov);
      // distance required for vertical fit
      const distV = halfH / Math.tan(vFov / 2);
      // horizontal fov derived from vertical fov & aspect
      const aspect = camera.aspect;
      const hFov = 2 * Math.atan(Math.tan(vFov / 2) * aspect);
      const distH = halfW / Math.tan(hFov / 2);
      const dist = Math.max(distV, distH);
      baseDistance = dist + extrudeDepth * 2;
      camera.position.set(0, 0, baseDistance * zoomScale);
      camera.lookAt(0, 0, 0);
    }

    function handleResize() {
      if (!mount) return;
      const { clientWidth, clientHeight } = mount;
      camera.aspect = clientWidth / clientHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(clientWidth, clientHeight);
      // We don't have extrudeDepth in this scope; assume same constant as used when creating geometry.
      fitCameraToText(0.3);
    }
    window.addEventListener("resize", handleResize);

    function applyZoom() {
      camera.position.z = baseDistance * zoomScale;
    }

    function handleKey(e: KeyboardEvent) {
      switch (e.key) {
        case "ArrowLeft":
          e.preventDefault();
          targetRoomRotationY -= 0.15; // rotate left
          break;
        case "ArrowRight":
          e.preventDefault();
          targetRoomRotationY += 0.15; // rotate right
          break;
        case "ArrowUp":
          e.preventDefault();
          // Zoom in (closer) - decrease zoomScale but keep a lower bound
          zoomScale = Math.max(0.3, zoomScale * 0.9);
          applyZoom();
          break;
        case "ArrowDown":
          e.preventDefault();
          // Zoom out (farther) - increase zoomScale but cap upper bound
          zoomScale = Math.min(6, zoomScale / 0.9);
          applyZoom();
          break;
      }
    }
    window.addEventListener("keydown", handleKey);

    // Raycaster for detecting clicks / hover on text
    const raycaster = new THREE.Raycaster();
    const pointer = new THREE.Vector2();

    function updatePointer(event: { clientX: number; clientY: number }) {
      const rect = renderer.domElement.getBoundingClientRect();
      pointer.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      pointer.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
    }

    function handlePointerMove(e: PointerEvent) {
      if (!interactiveTarget) return;
      updatePointer(e);
      raycaster.setFromCamera(pointer, camera);
      const hitsPrimary = raycaster.intersectObject(interactiveTarget, false);
      const hitsEphemeral = ephemeralCube.visible
        ? raycaster.intersectObject(ephemeralCube, false)
        : [];
      const hovering = hitsPrimary.length > 0 || hitsEphemeral.length > 0;
      if (hovering !== isHoveringText) {
        isHoveringText = hovering;
        hoverChanged = true;
      }
      renderer.domElement.style.cursor = hovering ? "pointer" : "default";
    }

    function handleClick(e: MouseEvent) {
      if (!interactiveTarget) return;
      updatePointer(e);
      raycaster.setFromCamera(pointer, camera);
      const hitsPrimary = raycaster.intersectObject(interactiveTarget, false);
      if (hitsPrimary.length) {
        navigate("/resume");
        return;
      }
      if (ephemeralCube.visible) {
        const hitsEphemeral = raycaster.intersectObject(ephemeralCube, false);
        if (hitsEphemeral.length) {
          currentAudio = playRandomTrack();
          enableMotionIfPossible();
          triggerEphemeralExplosion();
        }
      }
    }

    renderer.domElement.addEventListener("pointermove", handlePointerMove);
    renderer.domElement.addEventListener("click", handleClick);
    renderer.domElement.style.cursor = "default";

    // On first pointer down, attempt to enable motion (required user gesture for iOS)
    function firstPointerDown() {
      enableMotionIfPossible();
      renderer.domElement.removeEventListener("pointerdown", firstPointerDown);
    }
    renderer.domElement.addEventListener("pointerdown", firstPointerDown, {
      passive: true,
    });

    // --- Touch drag to rotate cube (horizontal swipe) & block default scroll/drag inside scene ---
    let isTouching = false;
    let lastTouchX = 0;
    let lastTouchY = 0;
    let touchStartX = 0;
    let touchStartY = 0;
    let touchMoved = false;
    const touchRotateFactor = 0.005; // sensitivity factor for converting px -> radians
    const touchTiltFactor = 0.005; // vertical sensitivity
    const maxTilt = Math.PI / 4; // clamp tilt to +/-45deg
    // Pinch zoom state
    let pinchActive = false;
    let pinchStartDistance = 0;
    let pinchStartZoom = 0;

    function distanceBetweenTouches(t1: Touch, t2: Touch) {
      const dx = t1.clientX - t2.clientX;
      const dy = t1.clientY - t2.clientY;
      return Math.hypot(dx, dy);
    }

    function handleTouchStart(e: TouchEvent) {
      if (e.touches.length === 1) {
        isTouching = true;
        lastTouchX = e.touches[0].clientX;
        lastTouchY = e.touches[0].clientY;
        touchStartX = lastTouchX;
        touchStartY = lastTouchY;
        touchMoved = false;
        pinchActive = false; // explicitly reset any prior pinch state
      } else if (e.touches.length === 2) {
        // Initialize pinch gesture
        pinchActive = true;
        isTouching = false; // disable single-finger rotate logic while pinching
        const d = distanceBetweenTouches(e.touches[0], e.touches[1]);
        pinchStartDistance = d;
        pinchStartZoom = zoomScale;
      }
      // Prevent potential scroll start when touching canvas
      e.preventDefault();
    }
    function handleTouchMove(e: TouchEvent) {
      // Pinch zoom handling
      if (pinchActive && e.touches.length === 2) {
        const newDist = distanceBetweenTouches(e.touches[0], e.touches[1]);
        if (pinchStartDistance > 0) {
          // ratio >1 when fingers move closer together? Actually smaller distance => ratio >1 if we invert.
          // We want: smaller distance => zoomScale increases (zoom out), larger distance => zoomScale decreases (zoom in).
          // Use factor = pinchStartDistance / newDist.
          const factor = pinchStartDistance / newDist;
          zoomScale = THREE.MathUtils.clamp(pinchStartZoom * factor, 0.3, 6);
          applyZoom();
        }
        e.preventDefault();
        return;
      }
      if (!isTouching || e.touches.length !== 1) return;
      const x = e.touches[0].clientX;
      const y = e.touches[0].clientY;
      const dx = x - lastTouchX;
      const dy = y - lastTouchY;
      lastTouchX = x;
      lastTouchY = y;
      if (!touchMoved) {
        const totalDx = x - touchStartX;
        const totalDy = y - touchStartY;
        if (Math.abs(totalDx) > 6 || Math.abs(totalDy) > 6) {
          touchMoved = true; // treat as drag, not tap
        }
      }
      targetRoomRotationY += dx * touchRotateFactor; // horizontal swipe -> yaw
      targetRoomRotationX = THREE.MathUtils.clamp(
        targetRoomRotationX + dy * touchTiltFactor,
        -maxTilt,
        maxTilt
      ); // vertical swipe -> pitch
      // Block page scrolling / dragging while interacting
      e.preventDefault();
    }
    function handleTouchEnd(e: TouchEvent) {
      // If pinch ended (fewer than two touches remain) stop pinch mode
      if (pinchActive && e.touches.length < 2) {
        pinchActive = false;
      }
      isTouching = false;
      // If it was a tap (no meaningful movement) treat as click on text
      if (!touchMoved && interactiveTarget) {
        updatePointer({ clientX: touchStartX, clientY: touchStartY });
        raycaster.setFromCamera(pointer, camera);
        const hitsPrimary = raycaster.intersectObject(interactiveTarget, false);
        if (hitsPrimary.length) {
          navigate("/resume");
        } else if (ephemeralCube.visible) {
          const hitsEphemeral = raycaster.intersectObject(ephemeralCube, false);
          if (hitsEphemeral.length) {
            currentAudio = playRandomTrack();
            triggerEphemeralExplosion();
          }
        }
      }
      e.preventDefault();
    }
    // Attach touch listeners directly to renderer DOM element for locality.
    renderer.domElement.addEventListener("touchstart", handleTouchStart, {
      passive: false,
    });
    renderer.domElement.addEventListener("touchmove", handleTouchMove, {
      passive: false,
    });
    renderer.domElement.addEventListener("touchend", handleTouchEnd, {
      passive: false,
    });
    renderer.domElement.addEventListener("touchcancel", handleTouchEnd, {
      passive: false,
    });
    // Disable default gesture actions (extra safety across browsers)
    renderer.domElement.style.touchAction = "none";

    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("keydown", handleKey);
      renderer.domElement.removeEventListener("pointermove", handlePointerMove);
      renderer.domElement.removeEventListener("click", handleClick);
      renderer.domElement.removeEventListener("pointerdown", firstPointerDown);
      renderer.domElement.removeEventListener("touchstart", handleTouchStart);
      renderer.domElement.removeEventListener("touchmove", handleTouchMove);
      renderer.domElement.removeEventListener("touchend", handleTouchEnd);
      renderer.domElement.removeEventListener("touchcancel", handleTouchEnd);
      if (mount.contains(renderer.domElement)) {
        mount.removeChild(renderer.domElement);
      }
      renderer.dispose();
      if (textMesh) {
        (textMesh.geometry as THREE.BufferGeometry).dispose();
        (textMesh.material as THREE.Material).dispose();
      }
      if (glowMesh) {
        (glowMesh.geometry as THREE.BufferGeometry).dispose();
        (glowMesh.material as THREE.Material).dispose();
      }
      if (buttonPlane) {
        (buttonPlane.geometry as THREE.BufferGeometry).dispose();
        (buttonPlane.material as THREE.Material).dispose();
      }
      if (buttonPlaneEdges) {
        (buttonPlaneEdges.geometry as THREE.BufferGeometry).dispose();
        (buttonPlaneEdges.material as THREE.Material).dispose();
      }
      ephemeralEdgeGeo.dispose();
      ephemeralEdgeMat.dispose();
      ephemeralGeometry.dispose();
      ephemeralMaterial.dispose();
      roomGeometry.dispose();
      roomMaterials.forEach((m) => {
        if (m.map) {
          m.map.dispose();
        }
        m.dispose();
      });
      roomEdgesGeometry.dispose();
      roomEdgeMaterial.dispose();
      if (currentAudio) {
        try {
          currentAudio.pause();
        } catch {}
        currentAudio = undefined;
      }
      if (motionActive) {
        window.removeEventListener("deviceorientation", handleOrientation);
      }
      // Fragment resources cleanup (if explosion mid-flight on unmount)
      fragments.forEach((f) => {
        fragmentGroup && fragmentGroup.remove(f.mesh);
        f.mat.dispose();
      });
      fragments = [];
      if (fragmentGeometry) {
        fragmentGeometry.dispose();
        fragmentGeometry = null;
      }
      if (fragmentGroup && scene) {
        scene.remove(fragmentGroup);
      }
      // Remove hidden canvas if we created it (leave if user added one elsewhere)
      if (hiddenCanvas && hiddenCanvas.parentElement === document.body) {
        document.body.removeChild(hiddenCanvas);
      }
    };
  }, []);

  return (
    <div
      style={{
        width: "100%",
        height: window.innerHeight, //"100vh",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div ref={mountRef} style={{ flex: 1 }} />
    </div>
  );
};

export default Home;
